/* ============================================================
   ss-base.js — shared behaviour for all ss-* sections
   Upload to: Online Store > Themes > Edit code > Assets > Add a new asset
   Loaded by: snippets/ss-head.liquid (defer)

   Everything here is idempotent and re-runnable, because the Shopify
   theme editor tears down and re-injects a section on every edit.
   Each module marks the nodes it has already wired with a data flag.
   ============================================================ */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- 1. Scroll reveal ---------- */
  function initReveal(root) {
    var nodes = (root || document).querySelectorAll('.ss-reveal:not([data-ss-reveal])');
    if (!nodes.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      nodes.forEach(function (n) { n.setAttribute('data-ss-reveal', '1'); n.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

    nodes.forEach(function (n, i) {
      n.setAttribute('data-ss-reveal', '1');
      n.style.transitionDelay = Math.min(i % 6, 5) * 70 + 'ms';
      io.observe(n);
    });
  }

  /* ---------- 2. Marquee: duplicate the track so the loop is seamless ----------
     The CSS animation translates the track by -50%, which only looks
     continuous if the content is present exactly twice. */
  function initMarquee(root) {
    (root || document).querySelectorAll('.ss-marquee__track:not([data-ss-marquee])').forEach(function (track) {
      track.setAttribute('data-ss-marquee', '1');
      var original = Array.prototype.slice.call(track.children);
      if (!original.length) return;
      original.forEach(function (child) {
        var clone = child.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        track.appendChild(clone);
      });
    });
  }

  /* ---------- 3. Tabs ----------
     Markup contract:
       [data-ss-tabs]
         button[data-ss-tab="key"][aria-selected]
         [data-ss-panel="key"][hidden] */
  function initTabs(root) {
    (root || document).querySelectorAll('[data-ss-tabs]:not([data-ss-tabs-ready])').forEach(function (wrap) {
      wrap.setAttribute('data-ss-tabs-ready', '1');
      var buttons = wrap.querySelectorAll('[data-ss-tab]');
      var panels = wrap.querySelectorAll('[data-ss-panel]');

      function select(key) {
        buttons.forEach(function (b) {
          var on = b.getAttribute('data-ss-tab') === key;
          b.setAttribute('aria-selected', on ? 'true' : 'false');
          b.tabIndex = on ? 0 : -1;
        });
        panels.forEach(function (p) {
          p.hidden = p.getAttribute('data-ss-panel') !== key;
        });
      }

      buttons.forEach(function (b, i) {
        b.addEventListener('click', function () { select(b.getAttribute('data-ss-tab')); });
        b.addEventListener('keydown', function (e) {
          var dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
          if (!dir) return;
          e.preventDefault();
          var next = buttons[(i + dir + buttons.length) % buttons.length];
          next.focus();
          select(next.getAttribute('data-ss-tab'));
        });
      });

      if (buttons.length) select(buttons[0].getAttribute('data-ss-tab'));
    });
  }

  /* ---------- 4. Accordion (FAQ) ----------
     Uses native <details>, so it degrades without JS. This only adds
     the "one open at a time" behaviour when the group opts in. */
  function initAccordion(root) {
    (root || document).querySelectorAll('[data-ss-accordion="single"]:not([data-ss-acc-ready])').forEach(function (group) {
      group.setAttribute('data-ss-acc-ready', '1');
      var items = group.querySelectorAll('details');
      items.forEach(function (d) {
        d.addEventListener('toggle', function () {
          if (!d.open) return;
          items.forEach(function (other) { if (other !== d) other.open = false; });
        });
      });
    });
  }

  /* ---------- 5. Rotating headline (the "We are passionate about ___" effect) ---------- */
  function initRotator(root) {
    (root || document).querySelectorAll('[data-ss-rotate]:not([data-ss-rotate-ready])').forEach(function (el) {
      el.setAttribute('data-ss-rotate-ready', '1');
      var words;
      try { words = JSON.parse(el.getAttribute('data-ss-rotate')); } catch (err) { return; }
      if (!Array.isArray(words)) return;
      // The list comes from a comma-separated theme setting, so entries
      // carry the space after each comma.
      words = words.map(function (w) { return String(w).trim(); }).filter(Boolean);
      if (words.length < 2) return;

      if (reduceMotion) { el.textContent = words[0]; return; }

      var i = 0;
      el.textContent = words[0];
      var timer = setInterval(function () {
        // Stop cleanly if the theme editor removed this node.
        if (!el.isConnected) { clearInterval(timer); return; }
        el.style.opacity = '0';
        el.style.transform = 'translateY(-10px)';
        setTimeout(function () {
          i = (i + 1) % words.length;
          el.textContent = words[i];
          el.style.opacity = '1';
          el.style.transform = 'none';
        }, 320);
      }, 2600);
    });
  }

  /* ---------- 6. Count-up stats ---------- */
  function initCounters(root) {
    var nodes = (root || document).querySelectorAll('[data-ss-count]:not([data-ss-count-ready])');
    if (!nodes.length) return;

    function run(el) {
      var target = parseFloat(el.getAttribute('data-ss-count'));
      if (isNaN(target)) return;
      var suffix = el.getAttribute('data-ss-count-suffix') || '';
      var duration = 1500;
      var start = null;
      function step(ts) {
        if (start === null) start = ts;
        var p = Math.min((ts - start) / duration, 1);
        // easeOutCubic
        var eased = 1 - Math.pow(1 - p, 3);
        el.textContent = Math.round(target * eased).toLocaleString() + suffix;
        if (p < 1) requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
    }

    if (reduceMotion || !('IntersectionObserver' in window)) {
      nodes.forEach(function (n) {
        n.setAttribute('data-ss-count-ready', '1');
        n.textContent = Number(n.getAttribute('data-ss-count')).toLocaleString() + (n.getAttribute('data-ss-count-suffix') || '');
      });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        run(e.target);
        io.unobserve(e.target);
      });
    }, { threshold: 0.4 });
    nodes.forEach(function (n) { n.setAttribute('data-ss-count-ready', '1'); io.observe(n); });
  }

  /* ---------- 7. Multi-step form ----------
     Markup contract:
       form[data-ss-steps]
         [data-ss-step="1"] ... [data-ss-step="2"]
         button[data-ss-next] / button[data-ss-prev]
         [data-ss-progress-bar] / [data-ss-progress-label] */
  function initSteps(root) {
    (root || document).querySelectorAll('[data-ss-steps]:not([data-ss-steps-ready])').forEach(function (form) {
      form.setAttribute('data-ss-steps-ready', '1');
      var steps = Array.prototype.slice.call(form.querySelectorAll('[data-ss-step]'));
      if (steps.length < 2) return;
      var bar = form.querySelector('[data-ss-progress-bar]');
      var label = form.querySelector('[data-ss-progress-label]');
      var current = 0;

      function render() {
        steps.forEach(function (s, i) { s.hidden = i !== current; });
        var pct = Math.round(((current + 1) / steps.length) * 100);
        if (bar) bar.style.width = pct + '%';
        if (label) label.textContent = 'Step ' + (current + 1) + ' of ' + steps.length;
      }

      // Only advance when the fields on THIS step are valid, otherwise the
      // browser would validate hidden step-2 fields and block submission
      // with an error the user cannot see.
      function stepIsValid() {
        var fields = steps[current].querySelectorAll('input, select, textarea');
        var ok = true;
        Array.prototype.forEach.call(fields, function (f) {
          if (f.type === 'hidden' || f.disabled) return;
          if (!f.checkValidity()) { if (ok) f.reportValidity(); ok = false; }
        });
        return ok;
      }

      form.querySelectorAll('[data-ss-next]').forEach(function (b) {
        b.addEventListener('click', function (e) {
          e.preventDefault();
          if (!stepIsValid()) return;
          current = Math.min(current + 1, steps.length - 1);
          render();
          form.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        });
      });
      form.querySelectorAll('[data-ss-prev]').forEach(function (b) {
        b.addEventListener('click', function (e) {
          e.preventDefault();
          current = Math.max(current - 1, 0);
          render();
        });
      });

      // Required fields on a hidden step must not block submit.
      form.addEventListener('submit', function () {
        steps.forEach(function (s, i) {
          if (i === current) return;
          s.querySelectorAll('[required]').forEach(function (f) { f.removeAttribute('required'); });
        });
      });

      render();
    });
  }

  /* ---------- 8. Sticky header state ---------- */
  function initStickyHeader(root) {
    (root || document).querySelectorAll('[data-ss-sticky]:not([data-ss-sticky-ready])').forEach(function (header) {
      header.setAttribute('data-ss-sticky-ready', '1');
      var stuck = null;
      var onScroll = function () {
        var now = window.scrollY > 24;
        if (now === stuck) return;
        stuck = now;
        header.classList.toggle('is-stuck', now);
      };
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
    });
  }

  /* ---------- 9. Mobile nav + mega menu ---------- */
  function initNav(root) {
    (root || document).querySelectorAll('[data-ss-nav]:not([data-ss-nav-ready])').forEach(function (nav) {
      nav.setAttribute('data-ss-nav-ready', '1');
      var toggle = nav.querySelector('[data-ss-nav-toggle]');
      var panel = nav.querySelector('[data-ss-nav-panel]');
      function close() {
        nav.classList.remove('is-open');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
        document.documentElement.style.overflow = '';
      }
      if (toggle && panel) {
        toggle.addEventListener('click', function () {
          var open = nav.classList.toggle('is-open');
          toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
          document.documentElement.style.overflow = open ? 'hidden' : '';
        });
        // A tapped link (e.g. "#contact" on the same page) must close the panel
        // and release the scroll lock, or the jump happens hidden behind it.
        panel.addEventListener('click', function (e) {
          var a = e.target.closest('a[href]');
          if (!a || !nav.classList.contains('is-open')) return;
          if (a.hasAttribute('data-ss-submenu-toggle') && window.innerWidth < 1100) return;
          close();
        });
      }
      // Submenu disclosure (click on mobile, hover handled by CSS on desktop)
      nav.querySelectorAll('[data-ss-submenu-toggle]').forEach(function (b) {
        b.addEventListener('click', function (e) {
          if (window.innerWidth >= 1100) return; // desktop opens on hover (matches ss-header breakpoint)
          e.preventDefault();
          var parent = b.closest('[data-ss-has-submenu]');
          if (!parent) return;
          var open = parent.classList.toggle('is-open');
          b.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
      });
      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape' || !nav.classList.contains('is-open')) return;
        close();
      });
    });
  }

  /* ---------- 10. Horizontal slider (testimonials, case studies) ---------- */
  function initSlider(root) {
    (root || document).querySelectorAll('[data-ss-slider]:not([data-ss-slider-ready])').forEach(function (wrap) {
      wrap.setAttribute('data-ss-slider-ready', '1');
      var track = wrap.querySelector('[data-ss-slider-track]');
      if (!track) return;
      var prev = wrap.querySelector('[data-ss-slider-prev]');
      var next = wrap.querySelector('[data-ss-slider-next]');

      function page() {
        var first = track.firstElementChild;
        return first ? first.getBoundingClientRect().width + 24 : track.clientWidth * 0.8;
      }
      function sync() {
        if (!prev || !next) return;
        var max = track.scrollWidth - track.clientWidth - 2;
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= max;
      }
      var auto = initAutoplay(wrap, track);
      if (prev) prev.addEventListener('click', function () { if (auto) auto.hold(); track.scrollBy({ left: -page(), behavior: 'smooth' }); });
      if (next) next.addEventListener('click', function () { if (auto) auto.hold(); track.scrollBy({ left: page(), behavior: 'smooth' }); });
      track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      sync();
    });
  }


  /* Continuous slow scroll for a slider with [data-ss-autoplay="<px per s>"].
     The slides are cloned once so the loop is seamless: when the scroll
     position passes the first copy it jumps back by exactly one copy's width,
     which looks identical. Pauses on hover, focus and touch, when off screen
     or in a background tab, and briefly after an arrow click. Skipped for
     reduced motion, in the theme editor (clones would confuse block
     selection) and when filter tabs are present (clones would break them). */
  function initAutoplay(wrap, track) {
    var speed = parseFloat(wrap.getAttribute('data-ss-autoplay'));
    if (!speed || reduceMotion) return null;
    if (window.Shopify && window.Shopify.designMode) return null;
    var host = wrap.closest('[data-ss-filter]');
    if (host && host.querySelector('[data-ss-filter-key]')) return null;
    var originals = Array.prototype.slice.call(track.children);
    if (originals.length < 2) return null;

    originals.forEach(function (li) {
      var c = li.cloneNode(true);
      c.setAttribute('aria-hidden', 'true');
      c.setAttribute('data-ss-clone', '1');
      c.removeAttribute('data-shopify-editor-block');
      // A clone of a not-yet-revealed card would never be observed and stay invisible.
      [c].concat(Array.prototype.slice.call(c.querySelectorAll('.ss-reveal'))).forEach(function (el) { el.classList.remove('ss-reveal'); });
      c.querySelectorAll('a, button, input, [tabindex]').forEach(function (el) { el.setAttribute('tabindex', '-1'); });
      track.appendChild(c);
    });
    wrap.classList.add('is-autoplay');

    var firstClone = track.children[originals.length];

    /* Performance: the loop width is measured once (and again on resize),
       never per frame. Reading offsetLeft every frame forced a layout 60
       times a second and was the single biggest main-thread cost on the
       page (Lighthouse, Oct 2026). Frames only run while the slider is on
       screen, and the loop starts after the page has loaded. */
    var width = 0;
    function measure() { width = firstClone.offsetLeft - originals[0].offsetLeft; }

    var pos = 0, last = 0, paused = false, heldUntil = 0, visible = false, raf = 0, started = false;
    function frame(t) {
      raf = 0;
      if (!visible || document.hidden) { last = 0; return; }
      var dt = last ? Math.min(t - last, 64) : 0;
      last = t;
      if (!paused && t > heldUntil) {
        pos += speed * dt / 1000;
        if (width > 0 && pos >= width) pos -= width;
        track.scrollLeft = Math.round(pos);
      }
      raf = requestAnimationFrame(frame);
    }
    function run() {
      if (!started || raf || !visible || document.hidden) return;
      pos = track.scrollLeft;
      if (width > 0 && pos >= width) { pos -= width; track.scrollLeft = pos; }
      raf = requestAnimationFrame(frame);
    }
    function pause() { paused = true; }
    function resume() { paused = false; pos = track.scrollLeft; }
    wrap.addEventListener('mouseenter', pause);
    wrap.addEventListener('mouseleave', resume);
    wrap.addEventListener('focusin', pause);
    wrap.addEventListener('focusout', resume);
    track.addEventListener('touchstart', pause, { passive: true });
    track.addEventListener('touchend', function () { heldUntil = performance.now() + 2500; resume(); }, { passive: true });
    track.addEventListener('scroll', function () { if (paused || performance.now() < heldUntil) pos = track.scrollLeft; }, { passive: true });
    window.addEventListener('resize', measure);
    document.addEventListener('visibilitychange', run);
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (e) { visible = e[0].isIntersecting; run(); }).observe(wrap);
    } else {
      visible = true;
    }
    function start() { started = true; measure(); run(); }
    if (document.readyState === 'complete') setTimeout(start, 1500);
    else window.addEventListener('load', function () { setTimeout(start, 1500); });
    return { hold: function () { heldUntil = performance.now() + 2500; } };
  }

  /* ---------- 11. Tag filter (testimonial tabs, work-page sidebar) ----------
     Markup contract:
       [data-ss-filter]
         button[data-ss-filter-key="seo"]      the FIRST button means "show all"
         [data-ss-tags="seo,ppc"]              items to show / hide
     Optional: [data-ss-filter-toggle] lets a second click on the active
     button clear the filter (the work page does this). */
  function initFilter(root) {
    (root || document).querySelectorAll('[data-ss-filter]:not([data-ss-filter-ready])').forEach(function (wrap) {
      wrap.setAttribute('data-ss-filter-ready', '1');
      var buttons = Array.prototype.slice.call(wrap.querySelectorAll('[data-ss-filter-key]'));
      var items = wrap.querySelectorAll('[data-ss-tags]');
      if (!buttons.length) return;
      var toggle = wrap.hasAttribute('data-ss-filter-toggle');
      var allKey = toggle ? null : buttons[0].getAttribute('data-ss-filter-key');

      function apply(key) {
        buttons.forEach(function (b) {
          var on = b.getAttribute('data-ss-filter-key') === key;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        items.forEach(function (it) {
          var tags = (it.getAttribute('data-ss-tags') || '').split(',');
          it.hidden = !(key === null || key === allKey || tags.indexOf(key) !== -1);
        });
        var track = wrap.querySelector('[data-ss-slider-track]');
        if (track) { track.scrollLeft = 0; track.dispatchEvent(new Event('scroll')); }
        var empty = wrap.querySelector('[data-ss-filter-empty]');
        if (empty) empty.hidden = Array.prototype.some.call(items, function (it) { return !it.hidden; });
      }

      buttons.forEach(function (b) {
        b.addEventListener('click', function () {
          var key = b.getAttribute('data-ss-filter-key');
          if (toggle && b.classList.contains('is-active')) key = null;
          apply(key);
        });
      });
    });
  }

  /* ---------- 12. Auto-reveal ----------
     Tags cards and rows below the first screen with .ss-reveal so they
     stagger in as the page scrolls; initReveal (module 1) then animates them.
     Anything already on screen is left alone (no flash), and the theme
     editor is skipped so merchants always see every block. */
  var AUTO_REVEAL = [
    '.ss-head', '.ss-svc__item', '.ss-fr__row', '.ss-it__media', '.ss-it__copy', '.ss-bn__item',
    '.ss-case', '.ss-wk__item', '.ss-cc__card', '.ss-proc__step', '.ss-stats__item', '.ss-fq__item',
    '.ss-tm__card', '.ss-cl__copy', '.ss-cl__media', '.ss-cta__body', '.ss-ct__card', '.ss-ct__aside',
    '.ss-lb__copy', '.ss-logos__item'
  ].join(',');
  function initAutoReveal(root) {
    if (window.Shopify && window.Shopify.designMode) return;
    var fold = window.innerHeight * 0.92;
    // All layout reads first, then all writes: interleaving them forced a
    // fresh style/layout pass for every element.
    var below = [];
    (root || document).querySelectorAll(AUTO_REVEAL).forEach(function (el) {
      if (el.hasAttribute('data-ss-auto') || el.classList.contains('ss-reveal')) return;
      if (el.closest('.ss-reveal')) return;
      below.push([el, el.getBoundingClientRect().top >= fold]);
    });
    below.forEach(function (pair) {
      pair[0].setAttribute('data-ss-auto', '1');
      if (pair[1]) pair[0].classList.add('ss-reveal');
    });
  }

  /* ---------- Boot ---------- */
  function initAll(root) {
    initAutoReveal(root);
    initReveal(root);
    initMarquee(root);
    initTabs(root);
    initAccordion(root);
    initRotator(root);
    initCounters(root);
    initSteps(root);
    initStickyHeader(root);
    initNav(root);
    initSlider(root);
    initFilter(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  // Theme editor: re-init only the section that was just re-rendered.
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:select', function (e) { initAll(e.target); });

  // Decorative animations (hero accents) start after load; see ss-base.css.
  function markReady() { setTimeout(function () { document.documentElement.classList.add('ss-ready'); }, 1200); }
  if (document.readyState === 'complete') markReady(); else window.addEventListener('load', markReady);

  window.SSBase = { init: initAll };
})();
